import { useEffect, useState } from 'react';
import { adminAPI, creditAPI } from '../../services/api';
import { SessionManager } from '../../utils/sessionManager';
import Layout from '../../components/Layout';
import APIUsageChart from '../../components/APIUsageChart';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider
} from '@mui/material';
import { AccountBalanceWallet, TrendingUp, People, Assessment } from '@mui/icons-material';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [adminCredits, setAdminCredits] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = SessionManager.getUser();

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [statsRes, creditsRes, adminStatsRes] = await Promise.all([
        adminAPI.getPlatformStats(),
        creditAPI.getBalance().catch(() => ({ data: { total: 0, used: 0, remaining: 0 } })),
        creditAPI.getStats().catch(() => ({ data: [] }))
      ]);

      setStats(statsRes.data);
      setAdminCredits(creditsRes.data);
      setAdminStats(adminStatsRes.data);
    } catch (error) {
      // console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Typography>Loading admin dashboard...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
          Admin Dashboard
        </Typography>

        {/* Admin's Personal Account Section */}
        {adminCredits && (
          <Card sx={{ mb: 4, bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceWallet /> My Account ({user?.firstName} {user?.lastName})
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Credits
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      ₹{adminCredits.total.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Credits Used
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#f57c00' }}>
                      ₹{adminCredits.used.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Credits Remaining
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#388e3c' }}>
                      ₹{adminCredits.remaining.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {adminStats && adminStats.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    My Verification Usage
                  </Typography>
                  <Grid container spacing={2}>
                    {adminStats.map((stat, index) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {stat.type}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {stat.count} calls
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ₹{stat.totalSpent.toFixed(2)} spent
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Platform Statistics */}
        {stats && (
          <>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Assessment /> Platform Statistics
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <People color="primary" />
                      <Typography color="text.secondary" variant="body2">
                        Total Users
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 600 }}>
                      {(stats.users.admin || 0) + (stats.users.client || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Admin: {stats.users.admin || 0} | Clients: {stats.users.client || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <People color="success" />
                      <Typography color="text.secondary" variant="body2">
                        Total Clients
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 600 }}>
                      {stats.users.client || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Registered clients
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Assessment color="info" />
                      <Typography color="text.secondary" variant="body2">
                        Total Verifications
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 600 }}>
                      {stats.verifications.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Success: {stats.verifications.successful} | Failed: {stats.verifications.failed}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUp color="warning" />
                      <Typography color="text.secondary" variant="body2">
                        Total Revenue
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 600 }}>
                      ₹{stats.revenue.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      From all verifications
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* API Usage Chart for Platform */}
            {stats.apiUsage && stats.apiUsage.length > 0 && (
              <APIUsageChart data={stats.apiUsage} title="Platform API Usages" />
            )}

            {/* Admin's Personal API Usage Chart */}
            {adminStats && adminStats.apiUsage && adminStats.apiUsage.length > 0 && (
              <APIUsageChart data={adminStats.apiUsage} title="My API Usages" />
            )}
          </>
        )}
      </Container>
    </Layout>
  );
};

export default AdminDashboard;
